import { keyboardRouteDefs, standardRouteDefs } from './paths';
import { hasAnyPermission, hasEveryPermission } from '../../shared/auth/permissions';

function normalizePath(pathname = '/') {
  const value = String(pathname || '/')
    .split(/[?#]/)[0]
    .trim();

  if (!value) {
    return '/';
  }

  const normalized = value.startsWith('/') ? value : `/${value}`;
  const trimmed = normalized.replace(/\/+$/, '');
  return trimmed || '/';
}

function splitSegments(pathname = '/') {
  const normalized = normalizePath(pathname);
  if (normalized === '/') {
    return [];
  }

  return normalized.slice(1).split('/');
}

function doesRouteMatchPath(routePath, pathname) {
  const routeSegments = splitSegments(routePath);
  const targetSegments = splitSegments(pathname);

  if (routeSegments.length !== targetSegments.length) {
    return false;
  }

  return routeSegments.every((segment, index) => {
    if (segment.startsWith(':')) {
      return Boolean(targetSegments[index]);
    }

    return segment === targetSegments[index];
  });
}

function getRouteSpecificity(routePath) {
  const routeSegments = splitSegments(routePath);

  return routeSegments.reduce(
    (score, segment) => score + (segment.startsWith(':') ? 1 : 10),
    routeSegments.length * 100
  );
}

function findMatchingRoute(pathname) {
  const normalizedPath = normalizePath(pathname);
  const isKeyboardPath = normalizedPath === '/keyboard' || normalizedPath.startsWith('/keyboard/');
  const candidatePath = isKeyboardPath
    ? normalizePath(normalizedPath.slice('/keyboard'.length) || '/')
    : normalizedPath;
  const definitions = isKeyboardPath ? keyboardRouteDefs : standardRouteDefs;

  const matches = definitions.filter((route) => doesRouteMatchPath(route.path, candidatePath));

  if (!matches.length) {
    return null;
  }

  return matches.sort((left, right) => getRouteSpecificity(right.path) - getRouteSpecificity(left.path))[0];
}

export function getPathAccessState(
  pathname,
  role,
  { enableAuth = true, permissions, authResolved = true } = {}
) {
  if (!enableAuth) {
    return { status: 'allowed', reason: 'auth-disabled' };
  }

  const route = findMatchingRoute(pathname);
  const requiredPermissionsAny = Array.isArray(route?.permissionsAny) ? route.permissionsAny : [];
  const requiredPermissionsAll = Array.isArray(route?.permissionsAll) ? route.permissionsAll : [];
  const isGovernedRoute = Boolean(requiredPermissionsAny.length || requiredPermissionsAll.length);

  if (!isGovernedRoute) {
    return { status: 'allowed', reason: 'ungoverned-route' };
  }

  if (!authResolved) {
    return { status: 'loading', reason: 'auth-pending' };
  }

  if (!hasAnyPermission(permissions, requiredPermissionsAny)) {
    return { status: 'denied', reason: 'permission-denied' };
  }

  if (!hasEveryPermission(permissions, requiredPermissionsAll)) {
    return { status: 'denied', reason: 'permission-denied' };
  }

  return { status: 'allowed', reason: 'permission-granted' };
}

export function canAccessPath(pathname, role, options = {}) {
  return getPathAccessState(pathname, role, options).status === 'allowed';
}

export function canCreateBillingDocuments(role, options) {
  return canAccessPath('/invoices/new', role, options);
}
